import { useEffect, useState } from 'react';
import { Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title } from '@tremor/react';
import { fetchQuoteConversion } from '../api/quoteconv';
import { t } from '../lib/i18n';
import type { QuoteConversionSummary } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
const fmtPct = new Intl.NumberFormat('es-MX', { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 0 });

/**
 * v2.33 — per-salesperson quote→invoice conversion, observed by the Go-side
 * quotetrack ticker. Hidden entirely when the endpoint is disabled (503) or
 * the viewer lacks view:sales (403).
 */
export default function QuoteConversionCard() {
  const [data, setData] = useState<QuoteConversionSummary | null>(null);

  useEffect(() => {
    let alive = true;
    fetchQuoteConversion(30).then((p) => { if (alive) setData(p); });
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const tot = data.totals;
  const resolved = tot.converted + tot.abandoned;

  return (
    <Card>
      <div className="flex flex-wrap items-baseline gap-x-3">
        <Title>{t.quoteConv.title}</Title>
        <Text className="text-tremor-content text-sm">
          {t.quoteConv.window.replace('{days}', String(data.window_days))}
          {' · '}
          {t.quoteConv.since.replace('{date}', data.tracking_since.slice(0, 10))}
        </Text>
      </div>
      <Text className="text-xs text-tremor-content">{t.quoteConv.subtitle}</Text>

      {resolved === 0 && tot.open_now === 0 && tot.created === 0 ? (
        <Text className="mt-3 text-tremor-content text-sm">{t.quoteConv.pending}</Text>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-4">
            <Kpi label={t.quoteConv.kpi.rate} value={tot.rate == null ? '—' : fmtPct.format(tot.rate)} />
            <Kpi label={t.quoteConv.kpi.converted} value={String(tot.converted)} />
            <Kpi label={t.quoteConv.kpi.abandoned} value={String(tot.abandoned)} />
            <Kpi label={t.quoteConv.kpi.avgDays} value={tot.avg_days_to_convert == null ? '—' : tot.avg_days_to_convert.toFixed(1)} />
            <Kpi label={t.quoteConv.kpi.convertedMxn} value={fmtMxn.format(tot.converted_mxn)} />
          </div>

          {data.people.length > 0 && (
            <Table className="mt-3">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.quoteConv.cols.slid}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.quoteConv.cols.created}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.quoteConv.cols.converted}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.quoteConv.cols.abandoned}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.quoteConv.cols.open}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.quoteConv.cols.rate}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.quoteConv.cols.avgDays}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.quoteConv.cols.mxn}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.people.map((p) => (
                  <TableRow key={p.slid}>
                    <TableCell>{p.slid}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.created}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">{p.converted}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600 dark:text-red-400">{p.abandoned}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.open_now}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.rate == null ? '—' : fmtPct.format(p.rate)}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.avg_days_to_convert == null ? '—' : p.avg_days_to_convert.toFixed(1)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMxn.format(p.converted_mxn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </Card>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
